import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Settings,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Clock,
  Database,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Play,
  ExternalLink,
  AlertTriangle,
  Lock,
  Unlock,
  X,
  Check,
  GitMerge,
  Code,
  Eye
} from 'lucide-react';

/**
 * Routing Admin Configuration
 * Allows admins to configure Google Sheets sync schedules and table mappings
 */
export default function RoutingAdminConfig() {
  const [activeTab, setActiveTab] = useState('sync'); // 'sync' or 'views'
  const [configs, setConfigs] = useState([]);
  const [derivedViews, setDerivedViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [message, setMessage] = useState(null);
  const [schemaModal, setSchemaModal] = useState(null); // { configId, config } for approval modal
  const [approving, setApproving] = useState(false);
  const [refreshingView, setRefreshingView] = useState(null);

  const db = getFirestore();

  // Count configs with pending schema changes
  const pendingSchemaCount = configs.filter(c => c.pendingSchemaChange?.requiresApproval).length;

  // Schedule options
  const scheduleOptions = [
    { value: 'manual', label: 'Manual Only (No Schedule)' },
    { value: 'every 30 minutes', label: 'Every 30 Minutes' },
    { value: 'every 1 hours', label: 'Every Hour' },
    { value: 'every 2 hours', label: 'Every 2 Hours' },
    { value: 'every 4 hours', label: 'Every 4 Hours' },
    { value: 'every 6 hours', label: 'Every 6 Hours' },
    { value: 'every 12 hours', label: 'Twice Daily' },
    { value: 'every 24 hours', label: 'Once Daily' },
  ];

  // Load configurations from Firestore
  useEffect(() => {
    loadConfigs();
    loadDerivedViews();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const configsRef = collection(db, 'routing_sync_configs');
      const snapshot = await getDocs(configsRef);

      const loadedConfigs = [];
      snapshot.forEach(doc => {
        loadedConfigs.push({ id: doc.id, ...doc.data() });
      });

      // Sort by name
      loadedConfigs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setConfigs(loadedConfigs);
    } catch (error) {
      console.error('Error loading configs:', error);
      setMessage({ type: 'error', text: 'Failed to load configurations' });
    } finally {
      setLoading(false);
    }
  };

  const loadDerivedViews = async () => {
    try {
      const viewsRef = collection(db, 'routing_derived_views');
      const snapshot = await getDocs(viewsRef);

      const loadedViews = [];
      snapshot.forEach(doc => {
        loadedViews.push({ id: doc.id, ...doc.data() });
      });

      loadedViews.sort((a, b) => (a.viewName || '').localeCompare(b.viewName || ''));
      setDerivedViews(loadedViews);
    } catch (error) {
      console.error('Error loading derived views:', error);
    }
  };

  // Extract sheet ID from URL
  const extractSheetId = (url) => {
    if (!url) return null;
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (match) return match[1];
    if (!url.includes('/') && url.length > 20) return url;
    return null;
  };

  // Add new configuration
  const addConfig = () => {
    const newConfig = {
      id: `config_${Date.now()}`,
      name: 'New Sync Configuration',
      sheetUrl: '',
      sheetId: '',
      tabName: '',
      tableName: '',
      schedule: 'manual',
      enabled: true,
      lastSync: null,
      lastSyncStatus: null,
      createdAt: new Date().toISOString()
    };
    setConfigs([...configs, newConfig]);
  };

  // Update configuration field
  const updateConfig = (id, field, value) => {
    setConfigs(configs.map(c => {
      if (c.id === id) {
        const updated = { ...c, [field]: value };
        // Auto-extract sheet ID when URL changes
        if (field === 'sheetUrl') {
          updated.sheetId = extractSheetId(value) || '';
        }
        return updated;
      }
      return c;
    }));
  };

  // Delete configuration
  const deleteConfig = async (id) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;

    try {
      await deleteDoc(doc(db, 'routing_sync_configs', id));
      setConfigs(configs.filter(c => c.id !== id));
      setMessage({ type: 'success', text: 'Configuration deleted' });
    } catch (error) {
      console.error('Error deleting config:', error);
      setMessage({ type: 'error', text: 'Failed to delete configuration' });
    }
  };

  // Save all configurations
  const saveConfigs = async () => {
    try {
      setSaving(true);

      for (const config of configs) {
        // Validate
        if (!config.sheetId) {
          setMessage({ type: 'error', text: `Please provide a valid Sheet URL for "${config.name}"` });
          return;
        }
        if (!config.tabName) {
          setMessage({ type: 'error', text: `Please provide a Tab Name for "${config.name}"` });
          return;
        }

        // Sanitize table name
        const sanitizedTableName = config.tableName
          ? config.tableName.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
          : `routing_${config.tabName.toLowerCase().replace(/[^a-z0-9_]/g, '_')}`;

        const configData = {
          name: config.name,
          sheetUrl: config.sheetUrl,
          sheetId: config.sheetId,
          tabName: config.tabName,
          tableName: sanitizedTableName,
          schedule: config.schedule,
          enabled: config.enabled,
          lastSync: config.lastSync || null,
          lastSyncStatus: config.lastSyncStatus || null,
          updatedAt: new Date().toISOString()
        };

        await setDoc(doc(db, 'routing_sync_configs', config.id), configData);
      }

      setMessage({ type: 'success', text: 'All configurations saved successfully!' });
      await loadConfigs();
    } catch (error) {
      console.error('Error saving configs:', error);
      setMessage({ type: 'error', text: 'Failed to save configurations' });
    } finally {
      setSaving(false);
    }
  };

  // Sync now
  const syncNow = async (config, forceApproveSchema = false) => {
    try {
      setSyncing(config.id);
      setMessage(null);

      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual');

      const result = await syncFunction({
        sheetId: config.sheetId,
        tabName: config.tabName,
        tableName: config.tableName || `routing_${config.tabName.toLowerCase().replace(/[^a-z0-9_]/g, '_')}`,
        configId: config.id,
        forceApproveSchema: forceApproveSchema
      });

      if (result.data?.schemaBlocked) {
        // Schema change detected - reload configs to show pending change
        await loadConfigs();
        setMessage({
          type: 'warning',
          text: `Schema change detected: +${result.data.added?.length || 0} columns added, ${result.data.removed?.length || 0} removed. Review required.`
        });
        return;
      }

      if (result.data?.success) {
        // Update last sync info
        updateConfig(config.id, 'lastSync', new Date().toISOString());
        updateConfig(config.id, 'lastSyncStatus', 'success');
        updateConfig(config.id, 'pendingSchemaChange', null);

        setMessage({
          type: 'success',
          text: `Synced ${result.data.rowsSynced} rows to ${result.data.tableName}`
        });

        // Reload to get updated locked schema
        await loadConfigs();
      } else {
        throw new Error(result.data?.error || 'Sync failed');
      }
    } catch (error) {
      console.error('Sync error:', error);
      updateConfig(config.id, 'lastSyncStatus', 'error');
      setMessage({ type: 'error', text: error.message || 'Sync failed' });
    } finally {
      setSyncing(null);
    }
  };

  // Approve schema change
  const approveSchemaChange = async (config) => {
    setApproving(true);
    try {
      await syncNow(config, true); // Force approve schema
      setSchemaModal(null);
      setMessage({ type: 'success', text: `Schema change approved for ${config.name}` });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setApproving(false);
    }
  };

  // Reject schema change (just clears the pending flag, doesn't sync)
  const rejectSchemaChange = async (config) => {
    try {
      await setDoc(doc(db, 'routing_sync_configs', config.id), {
        pendingSchemaChange: null,
        lastSyncStatus: 'schema_rejected',
        lastSyncError: 'Schema change rejected by admin'
      }, { merge: true });

      await loadConfigs();
      setSchemaModal(null);
      setMessage({ type: 'info', text: `Schema change rejected for ${config.name}. Data was not updated.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // Open Google Sheet
  const openSheet = (url) => {
    if (url) window.open(url, '_blank');
  };

  // ============== DERIVED VIEWS FUNCTIONS ==============

  // Add new derived view
  const addDerivedView = () => {
    const newView = {
      id: `view_${Date.now()}`,
      viewName: '',
      description: '',
      sqlQuery: '',
      sourceTables: [],
      enabled: true,
      autoRefreshAfterSync: true,
      lastRefresh: null,
      lastRefreshStatus: null,
      createdAt: new Date().toISOString()
    };
    setDerivedViews([...derivedViews, newView]);
  };

  // Update derived view
  const updateDerivedView = (id, field, value) => {
    setDerivedViews(derivedViews.map(v =>
      v.id === id ? { ...v, [field]: value } : v
    ));
  };

  // Delete derived view
  const deleteDerivedView = async (id) => {
    if (!window.confirm('Delete this derived view configuration?')) return;
    try {
      await deleteDoc(doc(db, 'routing_derived_views', id));
      setDerivedViews(derivedViews.filter(v => v.id !== id));
      setMessage({ type: 'success', text: 'View configuration deleted' });
    } catch (error) {
      console.error('Error deleting view:', error);
      setMessage({ type: 'error', text: 'Failed to delete view' });
    }
  };

  // Save derived views
  const saveDerivedViews = async () => {
    try {
      setSaving(true);

      for (const view of derivedViews) {
        if (!view.viewName) {
          setMessage({ type: 'error', text: 'Please provide a view name for all views' });
          return;
        }
        if (!view.sqlQuery) {
          setMessage({ type: 'error', text: `Please provide SQL query for "${view.viewName}"` });
          return;
        }

        const sanitizedViewName = view.viewName.toLowerCase()
          .replace(/[^a-z0-9_]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '');

        const viewData = {
          viewName: sanitizedViewName,
          description: view.description || '',
          sqlQuery: view.sqlQuery,
          sourceTables: view.sourceTables || [],
          enabled: view.enabled,
          autoRefreshAfterSync: view.autoRefreshAfterSync,
          lastRefresh: view.lastRefresh || null,
          lastRefreshStatus: view.lastRefreshStatus || null,
          updatedAt: new Date().toISOString()
        };

        await setDoc(doc(db, 'routing_derived_views', view.id), viewData);
      }

      setMessage({ type: 'success', text: 'All view configurations saved!' });
      await loadDerivedViews();
    } catch (error) {
      console.error('Error saving views:', error);
      setMessage({ type: 'error', text: 'Failed to save view configurations' });
    } finally {
      setSaving(false);
    }
  };

  // Refresh a derived view (create/update in BigQuery)
  const refreshView = async (view) => {
    try {
      setRefreshingView(view.id);
      setMessage(null);

      const functions = getFunctions();
      const createViewFn = httpsCallable(functions, 'createBigQueryView');

      const result = await createViewFn({
        viewName: view.viewName,
        sqlQuery: view.sqlQuery,
        configId: view.id
      });

      if (result.data?.success) {
        setMessage({
          type: 'success',
          text: `View "${result.data.viewName}" created/updated in BigQuery`
        });
        await loadDerivedViews();
      } else {
        throw new Error(result.data?.error || 'Failed to create view');
      }
    } catch (error) {
      console.error('Error refreshing view:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to refresh view' });
    } finally {
      setRefreshingView(null);
    }
  };

  // Get available source tables from sync configs
  const availableSourceTables = configs
    .filter(c => c.tableName)
    .map(c => c.tableName);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Settings size={24} className="text-slate-600" />
            Routing Configuration
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage data sources and derived views
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('sync')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'sync'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Database size={18} />
          Data Sources ({configs.length})
        </button>
        <button
          onClick={() => setActiveTab('views')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'views'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <GitMerge size={18} />
          Derived Views ({derivedViews.length})
        </button>
      </div>

      {/* Tab-specific action buttons */}
      <div className="flex justify-end gap-2">
        {activeTab === 'sync' && (
          <>
            <button
              onClick={addConfig}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={16} />
              Add Data Source
            </button>
            <button
              onClick={saveConfigs}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
              Save All
            </button>
          </>
        )}
        {activeTab === 'views' && (
          <>
            <button
              onClick={addDerivedView}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={16} />
              Add Derived View
            </button>
            <button
              onClick={saveDerivedViews}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
              Save All
            </button>
          </>
        )}
      </div>

      {/* Pending Schema Changes Alert */}
      {pendingSchemaCount > 0 && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-amber-600" size={20} />
            <div>
              <p className="font-medium text-amber-800">
                {pendingSchemaCount} schema change{pendingSchemaCount > 1 ? 's' : ''} pending approval
              </p>
              <p className="text-sm text-amber-600">
                Column changes detected in Google Sheets. Review and approve to continue syncing.
              </p>
            </div>
          </div>
          <span className="px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm font-medium">
            Action Required
          </span>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-700' :
          message.type === 'warning' ? 'bg-amber-50 text-amber-700' :
          message.type === 'info' ? 'bg-blue-50 text-blue-700' :
          'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> :
           message.type === 'warning' ? <AlertTriangle size={18} /> :
           <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* ==================== SYNC TAB ==================== */}
      {activeTab === 'sync' && (
        <>
          {/* Configurations */}
          {configs.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
              <FileSpreadsheet size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-500">No Data Sources</h3>
              <p className="text-slate-400 mt-2">Click "Add Data Source" to set up a Google Sheets sync</p>
            </div>
          ) : (
        <div className="space-y-4">
          {configs.map((config) => (
            <div key={config.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Config Header */}
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => updateConfig(config.id, 'enabled', e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-medium text-slate-800">
                    {config.tableName || config.name || 'New Config'}
                  </span>
                  {config.lockedSchema && (
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Lock size={10} />
                      {config.lockedSchema.columns?.length} cols
                    </span>
                  )}
                  {config.pendingSchemaChange?.requiresApproval && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <AlertTriangle size={10} />
                      Schema Change
                    </span>
                  )}
                  {config.lastSyncStatus === 'success' && !config.pendingSchemaChange?.requiresApproval && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Synced</span>
                  )}
                  {config.lastSyncStatus === 'error' && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Error</span>
                  )}
                  {config.lastSyncStatus === 'schema_blocked' && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Blocked</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {config.pendingSchemaChange?.requiresApproval ? (
                    <button
                      onClick={() => setSchemaModal(config)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
                    >
                      <AlertTriangle size={14} />
                      Review Changes
                    </button>
                  ) : (
                    <button
                      onClick={() => syncNow(config)}
                      disabled={syncing === config.id || !config.sheetId || !config.tabName}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 disabled:opacity-50 transition-colors"
                    >
                      {syncing === config.id ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Play size={14} />
                      )}
                      Sync Now
                    </button>
                  )}
                  <button
                    onClick={() => deleteConfig(config.id)}
                    className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Config Body */}
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sheet URL */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Google Sheet URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={config.sheetUrl}
                      onChange={(e) => updateConfig(config.id, 'sheetUrl', e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {config.sheetUrl && (
                      <button
                        onClick={() => openSheet(config.sheetUrl)}
                        className="px-2 text-slate-400 hover:text-blue-600"
                      >
                        <ExternalLink size={18} />
                      </button>
                    )}
                  </div>
                  {config.sheetId && (
                    <p className="text-xs text-slate-400 mt-1">ID: {config.sheetId}</p>
                  )}
                </div>

                {/* Tab Name */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Tab Name
                  </label>
                  <input
                    type="text"
                    value={config.tabName}
                    onChange={(e) => updateConfig(config.id, 'tabName', e.target.value)}
                    placeholder="e.g., Sheet1 or LAP Updates"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Table Name */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                    <Database size={12} />
                    BigQuery Table Name
                  </label>
                  <input
                    type="text"
                    value={config.tableName}
                    onChange={(e) => updateConfig(config.id, 'tableName', e.target.value)}
                    placeholder="e.g., routing_lap_updates"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Table will be created in inmocean_raw dataset
                  </p>
                </div>

                {/* Schedule */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                    <Clock size={12} />
                    Sync Schedule
                  </label>
                  <select
                    value={config.schedule}
                    onChange={(e) => updateConfig(config.id, 'schedule', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {scheduleOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {config.lastSync && (
                    <p className="text-xs text-slate-400 mt-1">
                      Last sync: {new Date(config.lastSync).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Important Notes:</h4>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Share Google Sheets with: <code className="bg-blue-100 px-1 rounded">169010327586-compute@developer.gserviceaccount.com</code></li>
              <li>Table names will be auto-sanitized (lowercase, underscores only)</li>
              <li>Scheduled syncs will overwrite existing data (WRITE_TRUNCATE)</li>
              <li>Schema is locked after first sync - column changes require admin approval</li>
              <li>Click "Save All" to persist your changes</li>
            </ul>
          </div>
        </>
      )}

      {/* ==================== VIEWS TAB ==================== */}
      {activeTab === 'views' && (
        <>
          {/* Derived Views */}
          {derivedViews.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
              <GitMerge size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-500">No Derived Views</h3>
              <p className="text-slate-400 mt-2">
                Create views that combine or transform your data sources
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {derivedViews.map((view) => (
                <div key={view.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {/* View Header */}
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={view.enabled}
                        onChange={(e) => updateDerivedView(view.id, 'enabled', e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <GitMerge size={18} className="text-purple-600" />
                      <input
                        type="text"
                        value={view.viewName}
                        onChange={(e) => updateDerivedView(view.id, 'viewName', e.target.value)}
                        className="font-medium text-slate-800 bg-transparent border-none focus:outline-none focus:ring-0"
                        placeholder="view_name"
                      />
                      {view.lastRefreshStatus === 'success' && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                      )}
                      {view.lastRefreshStatus === 'error' && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Error</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => refreshView(view)}
                        disabled={refreshingView === view.id || !view.viewName || !view.sqlQuery}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:opacity-50 transition-colors"
                      >
                        {refreshingView === view.id ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <Play size={14} />
                        )}
                        Create/Update View
                      </button>
                      <button
                        onClick={() => deleteDerivedView(view.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* View Body */}
                  <div className="p-4 space-y-4">
                    {/* Description */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                      <input
                        type="text"
                        value={view.description || ''}
                        onChange={(e) => updateDerivedView(view.id, 'description', e.target.value)}
                        placeholder="e.g., Combined BL and LAP routing data"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Source Tables */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Source Tables (for auto-refresh after sync)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {availableSourceTables.map(tableName => (
                          <label key={tableName} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                            <input
                              type="checkbox"
                              checked={(view.sourceTables || []).includes(tableName)}
                              onChange={(e) => {
                                const current = view.sourceTables || [];
                                const updated = e.target.checked
                                  ? [...current, tableName]
                                  : current.filter(t => t !== tableName);
                                updateDerivedView(view.id, 'sourceTables', updated);
                              }}
                              className="h-3 w-3 rounded border-slate-300 text-blue-600"
                            />
                            <span className="text-sm text-slate-700">{tableName}</span>
                          </label>
                        ))}
                        {availableSourceTables.length === 0 && (
                          <span className="text-sm text-slate-400">No data sources configured yet</span>
                        )}
                      </div>
                    </div>

                    {/* SQL Query */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        <Code size={12} className="inline mr-1" />
                        SQL Query
                      </label>
                      <textarea
                        value={view.sqlQuery || ''}
                        onChange={(e) => updateDerivedView(view.id, 'sqlQuery', e.target.value)}
                        placeholder={`SELECT DISTINCT
  pick_ticket,
  route_date,
  loads,
  ship_date
FROM (
  SELECT pick_ticket, routed_date AS route_date, loads, ship_date FROM inmocean_raw.lap
  UNION ALL
  SELECT pick_ticket, route_date, loads, ship_date FROM inmocean_raw.bl
)`}
                        rows={8}
                        className="w-full px-3 py-2 text-sm font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Auto-refresh option */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`refresh-${view.id}`}
                        checked={view.autoRefreshAfterSync}
                        onChange={(e) => updateDerivedView(view.id, 'autoRefreshAfterSync', e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600"
                      />
                      <label htmlFor={`refresh-${view.id}`} className="text-sm text-slate-600">
                        Auto-refresh after source table syncs
                      </label>
                    </div>

                    {/* Last refresh info */}
                    {view.lastRefresh && (
                      <div className="text-xs text-slate-400">
                        Last refresh: {new Date(view.lastRefresh).toLocaleString()}
                        {view.lastRefreshError && (
                          <span className="text-red-500 ml-2">Error: {view.lastRefreshError}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Help Text for Views */}
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
            <h4 className="text-sm font-medium text-purple-800 mb-2">About Derived Views:</h4>
            <ul className="text-sm text-purple-700 space-y-1 list-disc list-inside">
              <li>Views are SQL queries stored in BigQuery that combine or transform your data</li>
              <li>Views always show the latest data from source tables (no sync needed)</li>
              <li>Select source tables to auto-refresh the view after those tables sync</li>
              <li>Use <code className="bg-purple-100 px-1 rounded">inmocean_raw.table_name</code> to reference tables</li>
            </ul>
          </div>
        </>
      )}

      {/* Schema Change Approval Modal */}
      {schemaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-amber-50">
              <div className="flex items-center gap-3">
                <AlertTriangle className="text-amber-600" size={24} />
                <div>
                  <h3 className="font-semibold text-slate-800">Schema Change Detected</h3>
                  <p className="text-sm text-slate-600">{schemaModal.name}</p>
                </div>
              </div>
              <button
                onClick={() => setSchemaModal(null)}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <p className="text-slate-600 mb-4">
                The columns in the Google Sheet have changed since the last sync.
                Review the changes below and decide whether to approve or reject.
              </p>

              {/* Added Columns */}
              {schemaModal.pendingSchemaChange?.added?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                    <Plus size={14} />
                    Added Columns ({schemaModal.pendingSchemaChange.added.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {schemaModal.pendingSchemaChange.added.map((col, idx) => (
                      <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Removed Columns */}
              {schemaModal.pendingSchemaChange?.removed?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                    <Trash2 size={14} />
                    Removed Columns ({schemaModal.pendingSchemaChange.removed.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {schemaModal.pendingSchemaChange.removed.map((col, idx) => (
                      <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 text-sm rounded line-through">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Current vs New columns comparison */}
              <details className="mt-4">
                <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700">
                  View full column list
                </summary>
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-xs font-medium text-slate-500 mb-2">Current Schema ({schemaModal.lockedSchema?.columns?.length || 0} cols)</h5>
                    <div className="bg-slate-50 rounded p-2 text-xs text-slate-600 max-h-40 overflow-y-auto">
                      {schemaModal.lockedSchema?.columns?.map((col, idx) => (
                        <div key={idx} className="py-0.5">{col}</div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-slate-500 mb-2">New Schema ({schemaModal.pendingSchemaChange?.newColumns?.length || 0} cols)</h5>
                    <div className="bg-slate-50 rounded p-2 text-xs text-slate-600 max-h-40 overflow-y-auto">
                      {schemaModal.pendingSchemaChange?.newColumns?.map((col, idx) => (
                        <div key={idx} className="py-0.5">{col}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </details>

              {/* Warning */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                <p className="text-sm text-amber-700">
                  <strong>Warning:</strong> Approving will update the locked schema and sync the data.
                  Rejecting will keep the current schema and block further syncs until approved.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50">
              <button
                onClick={() => rejectSchemaChange(schemaModal)}
                disabled={approving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                <X size={16} />
                Reject
              </button>
              <button
                onClick={() => approveSchemaChange(schemaModal)}
                disabled={approving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {approving ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                {approving ? 'Approving...' : 'Approve & Sync'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
